import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

export interface NewsQuery {
  and: string;
  or: string;
  not: string;
  phrase: string;
}

export const EMPTY_NEWS_QUERY: NewsQuery = { and: '', or: '', not: '', phrase: '' };

export function parseNewsQuery(raw: unknown): NewsQuery {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_NEWS_QUERY };
  const q = raw as Record<string, unknown>;
  return {
    and: String(q.and || ''),
    or: String(q.or || ''),
    not: String(q.not || ''),
    phrase: String(q.phrase || ''),
  };
}

export function newsQueryHasTerms(q: NewsQuery): boolean {
  return Boolean(q.and.trim() || q.or.trim() || q.not.trim() || q.phrase.trim());
}

const OPERATOR = /^(AND|OR|NOT)$/i;

function splitWords(raw: string): string[] {
  return raw.split(/\s+/).map((w) => w.trim()).filter((w) => w && !OPERATOR.test(w));
}

/** Split on the word OR; each clause can be multiple words. */
export function splitOrClauses(raw: string): string[] {
  return raw
    .split(/\s+OR\s+/i)
    .map((part) => part.replace(/^\s*OR\s+|\s+OR\s*$/gi, '').trim())
    .filter((part) => part && !OPERATOR.test(part));
}

function quoteIfMulti(value: string): string {
  return /\s/.test(value) ? `"${value}"` : value;
}

export function formatNewsQueryPreview(q: NewsQuery): string {
  const parts: string[] = [];
  const andWords = splitWords(q.and);
  const orClauses = splitOrClauses(q.or);
  const notWords = splitWords(q.not);
  const phrase = q.phrase.trim();
  if (andWords.length) parts.push(andWords.join(' '));
  if (orClauses.length) {
    const or = orClauses.map(quoteIfMulti).join(' OR ');
    parts.push(parts.length ? `(${or})` : or);
  }
  if (phrase) parts.push(`"${phrase}"`);
  if (notWords.length) {
    parts.push(notWords.map((w) => `-${w}`).join(' '));
  }
  return parts.join(' ').trim();
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.04)',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
    '&.Mui-focused fieldset': { borderColor: '#e53935' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiFormHelperText-root': { mx: 0, mt: 0.5, color: 'rgba(255,255,255,0.45)' },
};

const HINTS = [
  'word word = both required',
  'a OR b = either',
  '-word = exclude',
  '"exact phrase"',
];

interface NewsQueryBuilderDialogProps {
  open: boolean;
  initial: NewsQuery;
  onClose: () => void;
  onApply: (query: NewsQuery) => void;
}

const NewsQueryBuilderDialog: React.FC<NewsQueryBuilderDialogProps> = ({
  open,
  initial,
  onClose,
  onApply,
}) => {
  const [draft, setDraft] = useState<NewsQuery>(initial);
  const preview = useMemo(() => formatNewsQueryPreview(draft), [draft]);

  const setField = (key: keyof NewsQuery) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEnter: () => setDraft(initial) }}
      PaperProps={{
        sx: {
          bgcolor: '#121212',
          backgroundImage: 'linear-gradient(180deg, rgba(229,57,53,0.08), transparent 28%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AutoFixHighIcon sx={{ color: '#e53935' }} />
        Query builder
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: '8px !important' }}>
        <Typography variant="caption" color="text.secondary">
          Filters DataHog media inside the packet geofence (plus buffer). Plain terms search headlines and body.
        </Typography>
        <TextField
          label="All of these words (AND)"
          value={draft.and}
          onChange={setField('and')}
          helperText="Main terms. All must appear."
          size="small"
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="Any of these words (OR)"
          value={draft.or}
          onChange={setField('or')}
          helperText="Separate alternatives with OR. Multi-word groups stay together."
          size="small"
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="None of these words (NOT)"
          value={draft.not}
          onChange={setField('not')}
          helperText="Documents containing these are excluded."
          size="small"
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="Exact phrase"
          value={draft.phrase}
          onChange={setField('phrase')}
          helperText="These words must appear together, in this order."
          size="small"
          fullWidth
          sx={fieldSx}
        />
        <Box>
          <Typography variant="caption" sx={{ letterSpacing: 1, color: 'rgba(255,255,255,0.45)', fontWeight: 700 }}>
            QUERY PREVIEW
          </Typography>
          <Box
            sx={{
              mt: 0.75,
              px: 1.5,
              py: 1.25,
              minHeight: 44,
              borderRadius: 2,
              bgcolor: 'rgba(0,0,0,0.45)',
              border: '1px solid rgba(255,255,255,0.08)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.8rem',
              color: preview ? '#fff' : 'rgba(255,255,255,0.35)',
            }}
          >
            {preview || '—'}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            bgcolor: 'rgba(33,150,243,0.12)',
            border: '1px solid rgba(33,150,243,0.25)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(187,222,251,0.95)', lineHeight: 1.45 }}>
            Terms apply on every OSINT collect. Add OR, exclusions, or a phrase to tighten the match.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {HINTS.map((hint) => (
            <Box
              key={hint}
              sx={{
                px: 1,
                py: 0.4,
                borderRadius: 10,
                bgcolor: '#000',
                border: '1px solid rgba(255,255,255,0.12)',
                fontSize: '0.65rem',
                color: 'rgba(255,255,255,0.75)',
              }}
            >
              {hint}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#e53935', textTransform: 'none' }}>Cancel</Button>
        <Button
          onClick={() => onApply(draft)}
          variant="contained"
          sx={{
            textTransform: 'none',
            bgcolor: newsQueryHasTerms(draft) ? '#2a2a2a' : '#1a1a1a',
            color: '#fff',
            '&:hover': { bgcolor: '#3a3a3a' },
          }}
        >
          {newsQueryHasTerms(draft) ? 'Apply terms' : 'Clear terms'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsQueryBuilderDialog;
